// ENG-5963: the maintenance-window + urgent-escape-hatch decision layer that
// sits ABOVE the existing self-update coalesce gate (self-update-coalesce.ts)
// and the Claude Code updater's idle gate. Pure and dependency-light so the
// gate ordering is unit-testable; the I/O (registry fetch, install) stays at
// the manager call sites.
//
// Ordering at each restart-causing update site:
//   1. urgent dist-tag newer than installed?  → install now, bypass everything
//   2. within the maintenance window?          → no: defer this cycle
//   3. (existing) coalesce / idle gate         → then apply

import {
  isWithinMaintenanceWindow,
  isMaintenanceWindowDisabled,
  type MaintenanceWindowInput,
} from './maintenance-window.js';
import {
  FORCED_UPDATE_RELAX_AFTER_MS,
  forcedUpdatePendingForMs,
  isForcedUpdatePastDeadline,
} from '@augmented/core';

/** The resolved window the manager caches off the heartbeat response. */
export type CachedMaintenanceWindow = MaintenanceWindowInput;

/**
 * Window gate for a (non-urgent) restart-causing update.
 *  - 'proceed' → in-window, or gating disabled, or no window known yet
 *    (fail-open so a host that hasn't heard from the API isn't frozen out).
 *  - 'defer'   → a window is known and we're currently outside it.
 */
export function decideMaintenanceWindowGate(opts: {
  window: CachedMaintenanceWindow | null;
  now: Date;
  env?: NodeJS.ProcessEnv;
}): 'proceed' | 'defer' {
  if (isMaintenanceWindowDisabled(opts.env)) return 'proceed';
  if (!opts.window) return 'proceed';
  return isWithinMaintenanceWindow(opts.window, opts.now) ? 'proceed' : 'defer';
}

// ENG-6692: the admin "Update CLI now" one-shot. The API delivers
// hosts.update_requested_at on the heartbeat; the manager runs ONE
// window-bypassing self-update per distinct timestamp (dedup on a persisted
// marker so the fire survives the manager restart the update itself triggers),
// but still defers until the host is idle so a live turn is never interrupted.

export type ForcedUpdateDecision =
  /** No pending "Update CLI now" request on this heartbeat. */
  | 'none'
  /** Already ran for this (or a newer) timestamp — dedup. */
  | 'already-handled'
  /** An agent on the host is mid-turn / recently active — retry next poll. */
  | 'defer-idle'
  /** Run the window-bypassing self-update now (host idle, fresh request). */
  | 'consume'
  /**
   * ENG-8449: run it now even though the host still reads busy — the request has
   * been pending past {@link FORCED_UPDATE_HARD_DEADLINE_MS}. Distinct from
   * 'consume' so the caller can log the override honestly (and so a test can
   * prove the deadline fired rather than the host merely having gone quiet).
   */
  | 'consume-deadline';

/**
 * ENG-6771: the outcome of a self-update attempt, so the forced "Update CLI now"
 * caller knows whether to consume its one-shot request.
 *   - 'updated' : committed to an install (restart scheduled).
 *   - 'noop'    : nothing to do (already on the latest in-channel build, dev
 *                 mode, unknown install method, or a non-forced defer) — a
 *                 legitimate completion, so the one-shot is consumed.
 *   - 'failed'  : the attempt failed BEFORE any install committed (registry
 *                 fetch/timeout/non-200, npm install error, post-install smoke
 *                 check) — the one-shot is NOT consumed, so the next idle
 *                 heartbeat retries instead of silently burning the request.
 * Non-forced callers ignore the value.
 */
export type SelfUpdateOutcome = 'updated' | 'noop' | 'failed';

/**
 * ENG-6771: whether a forced "Update CLI now" request should be consumed (its
 * dedup marker advanced so the heartbeat acks it and the API clears
 * hosts.update_requested_at) given the self-update outcome. Only a genuine
 * completion consumes the one-shot: 'updated' or 'noop'. A 'failed' attempt must
 * NOT consume.
 *
 * The bug this guards against (ENG-6771): the old code advanced the dedup marker
 * BEFORE kicking the updater, so a transient npm registry timeout (or any
 * pre-install failure) burned the request silently — the heartbeat acked it, the
 * API cleared the column, and nothing retried while the webapp had shown success.
 */
export function shouldConsumeForcedUpdate(outcome: SelfUpdateOutcome): boolean {
  return outcome !== 'failed';
}

/**
 * ENG-8079: how many consecutive `defer-idle` decisions a pending "Update CLI
 * now" tolerates before the idle gate relaxes to the progress axis only.
 *
 * The starvation this bounds: `isHostBusyForForcedUpdate` is OR-ed across EVERY
 * agent on the host, and one of its two axes is "an inbound message arrived less
 * than RESTART_INBOUND_QUIET_SECONDS (300s) ago". On a host with a dozen-plus
 * agents there is almost always one that received something in the last five
 * minutes, so the host reads as busy essentially forever — 313 consecutive
 * defers were observed on agt-aws-1 for a single request. Polls land roughly
 * every 20-30s, so 20 defers is ~7-10 minutes of genuinely trying to be polite
 * before we stop waiting for a quiet window that may never come.
 */
export const FORCED_UPDATE_DEFER_ESCALATE_AFTER = 20;

/**
 * ENG-8449: the two wall-clock thresholds a pending "Update CLI now" escalates
 * through, plus the pure helpers that read them, live in `@augmented/core` —
 * the console renders the same deadline on the host Overview chip, and two
 * copies of the number would drift silently (the chip would describe a deadline
 * the host is not keeping). Re-exported here so the gate module stays the single
 * import site for everything the manager's forced-update path needs.
 */
export {
  FORCED_UPDATE_RELAX_AFTER_MS,
  FORCED_UPDATE_HARD_DEADLINE_MS,
  forcedUpdatePendingForMs,
  isForcedUpdatePastDeadline,
} from '@augmented/core';

/**
 * ENG-8079: should a pending forced update drop the inbound-quiet axis and gate
 * on progress alone? True once the defer streak reaches
 * `FORCED_UPDATE_DEFER_ESCALATE_AFTER`, or (ENG-8449) once the request has been
 * pending `FORCED_UPDATE_RELAX_AFTER_MS` by the wall clock.
 *
 * Relaxing drops ONLY the inbound axis (a message arrived recently). The
 * progress axis — transcript JSONL growth, with pane.log mtime as the fallback —
 * still gates, so an agent actually producing tokens is never interrupted. That
 * is the safety property ENG-6692 cared about; "someone sent a message five
 * minutes ago" was never on its own evidence of a live turn.
 *
 * The two routes are OR-ed rather than swapped: on a healthy manager the streak
 * still gets there first and behaviour is unchanged, while a manager that keeps
 * restarting — the case where the streak silently rewinds to 0 — now escalates
 * anyway. That is what stops the streak being load-bearing (ENG-8449 AC5); it
 * survives only as an accelerator and a log counter.
 */
export function shouldRelaxForcedUpdateIdleGate(
  deferStreak: number,
  pendingForMs?: number | null,
): boolean {
  if (deferStreak >= FORCED_UPDATE_DEFER_ESCALATE_AFTER) return true;
  return pendingForMs !== null && pendingForMs !== undefined && pendingForMs >= FORCED_UPDATE_RELAX_AFTER_MS;
}

/**
 * Pure decision for the heartbeat consume block: given the timestamp the API
 * delivered, the last one this manager consumed, and whether the host is busy,
 * should we run the forced self-update? Extracted from manager-worker.ts so the
 * dedup + idle ordering is unit-testable (the I/O — persist marker, kick the
 * updater — stays at the call site).
 *
 * Dedup uses `>=` on parsed timestamps so a re-delivered (or out-of-order
 * older) request can't re-fire; only a strictly newer request after a prior
 * consume re-arms. An unparseable timestamp is treated as 'none' (fail safe —
 * never act on a value we can't compare).
 *
 * ENG-8449: `pastDeadline` (from {@link isForcedUpdatePastDeadline}) overrides a
 * busy host, returning 'consume-deadline'. It is checked AFTER dedup on purpose:
 * a request already consumed stays 'already-handled' however old it is, so an
 * expired-but-handled timestamp can never re-fire an update.
 */
export function decideForcedUpdate(opts: {
  requestedAt: string | null | undefined;
  lastProcessedAt: string | null | undefined;
  hostBusy: boolean;
  /**
   * ENG-8449: pending past the hard ceiling — stop consulting the idle gate.
   *
   * Required, not optional, deliberately. An optional flag can be dropped at the
   * call site with no compile error and no failing test — the manager would go
   * on deferring forever and the only evidence would be the absence of a log
   * line nobody is watching for. Making it required means the one call site
   * cannot forget to compute it.
   */
  pastDeadline: boolean;
}): ForcedUpdateDecision {
  const { requestedAt, lastProcessedAt, hostBusy, pastDeadline } = opts;
  if (!requestedAt) return 'none';
  const requestedMs = Date.parse(requestedAt);
  if (Number.isNaN(requestedMs)) return 'none';
  if (lastProcessedAt) {
    const processedMs = Date.parse(lastProcessedAt);
    if (!Number.isNaN(processedMs) && processedMs >= requestedMs) return 'already-handled';
  }
  if (hostBusy) return pastDeadline ? 'consume-deadline' : 'defer-idle';
  return 'consume';
}

/** ENG-8449: everything the manager needs to act on and log one poll's verdict. */
export interface PendingForcedUpdateVerdict {
  decision: ForcedUpdateDecision;
  /** Wall-clock age of the request; null when none is pending. For the log line. */
  pendingForMs: number | null;
  /** Whether the idle gate was consulted with the inbound axis dropped. */
  relaxed: boolean;
  /** Whether the hard ceiling was reached (i.e. the gate was bypassed outright). */
  pastDeadline: boolean;
}

/**
 * ENG-8449: the whole forced-update decision for one poll, composed here rather
 * than at the manager call site.
 *
 * Why composed: the manager previously did this in four steps — age the request,
 * derive `relaxed`, derive `pastDeadline`, then call `decideForcedUpdate`. Each
 * was a place the wiring could quietly regress, and none of it was reachable by
 * a unit test (`runPendingForcedUpdate` sits deep inside manager-worker.ts with
 * module-level state and I/O either side). A sabotage run confirmed it: replacing
 * the age computation with a hard-coded `null` — reinstating the exact starvation
 * this issue fixes — passed both the type check and every test. Moving the
 * composition here leaves the manager nothing to compute and this function
 * everything to test.
 *
 * `isHostBusy` is a callback rather than a boolean because relaxed-ness has to be
 * decided BEFORE the busy check runs — it changes which axes that check consults.
 * Passing a pre-computed boolean would silently gate on the wrong axes.
 */
export function decidePendingForcedUpdate(opts: {
  requestedAt: string | null | undefined;
  lastProcessedAt: string | null | undefined;
  /** ENG-8079's counter. Only an accelerator now — the clock is the guarantee. */
  deferStreak: number;
  nowMs: number;
  isHostBusy: (relaxed: boolean) => boolean;
}): PendingForcedUpdateVerdict {
  const pendingForMs = forcedUpdatePendingForMs({
    requestedAt: opts.requestedAt,
    nowMs: opts.nowMs,
  });
  const relaxed = shouldRelaxForcedUpdateIdleGate(opts.deferStreak, pendingForMs);
  const pastDeadline = isForcedUpdatePastDeadline(pendingForMs);
  const decision = decideForcedUpdate({
    requestedAt: opts.requestedAt,
    lastProcessedAt: opts.lastProcessedAt,
    hostBusy: opts.isHostBusy(relaxed),
    pastDeadline,
  });
  return { decision, pendingForMs, relaxed, pastDeadline };
}

/**
 * Does the `urgent` dist-tag warrant an immediate, window-and-coalesce-bypassing
 * install? True only when an urgent target exists and is strictly newer than
 * what's installed. Channel-independent by construction: there is no channel
 * input, so a channel-pinned host (AGT_CLI_RELEASE_CHANNEL=test) honours urgent
 * exactly like a `latest` host — urgent is a fleet-wide hotfix override.
 * `dev` builds (no injected version) never urgent-upgrade.
 */
export function isUrgentUpgrade(opts: {
  urgentTarget: string | null;
  installed: string;
  isNewer: (installed: string, candidate: string) => boolean;
}): boolean {
  if (!opts.urgentTarget) return false;
  if (opts.installed === 'dev') return false;
  return opts.isNewer(opts.installed, opts.urgentTarget);
}

// ENG-5963 (-4): gating for restart-CAUSING maintenance (agent-session
// restarts), distinct from the update gates above.

/** An agent whose pane.log was touched within this many seconds is "busy"
 * (mid-turn / recently producing output) and a non-urgent restart defers. */
export const RESTART_IDLE_THRESHOLD_SECONDS = 120;
/** ...and an agent that received an inbound message within this many seconds is
 * also "busy" — it covers the gap where a message just arrived but the agent
 * hasn't produced pane output yet. Idle requires BOTH quiet. */
export const RESTART_INBOUND_QUIET_SECONDS = 300;
/**
 * ENG-6264: transcript-JSONL age (s) at/under which the session is "producing"
 * (actively streaming tokens) and a restart defers. This is the PRIMARY progress
 * signal — pane.log mtime is only the fallback, because an animated frozen
 * spinner keeps pane.log fresh without producing anything (the failure that let
 * the reaper interrupt busy agents).
 *
 * ENG-8196: this NO LONGER mirrors the wedge detector's `transcriptStaleSeconds`,
 * and the divergence is deliberate. That default was raised 60s -> 900s so a
 * single long tool call stops reading as a frozen session (a >60s tool call
 * writes a `tool_use` entry and then nothing until `tool_result` returns, while
 * the pane streams the whole time — jarvis was killed three times mid-turn at
 * `paneAge=0s transcriptAge=113s`).
 *
 * This constant stays at 60 because it gates something different: maintenance-
 * window restart DEFERRAL, not a kill. At 900s virtually every agent would read
 * "busy" for a 15-minute window, and since deferral re-arms on each idle check
 * that could starve the update window entirely — an agent that never takes a
 * CLI self-update is a worse outcome than a maintenance restart landing slightly
 * sooner than ideal. The two knobs answer different questions ("is it safe to
 * defer a restart?" vs "is this session hung?"), so one shared "busy" notion was
 * the wrong framing rather than a value that drifted.
 *
 * If you change either one, do NOT assume the other should follow. */
export const RESTART_TRANSCRIPT_STALE_SECONDS = 60;

// Allowlist of RestartReason values that count as NON-URGENT maintenance and
// are therefore gated to the window + defer-until-idle. Anything not listed
// (day-rollover, auth-tuple-change recovery, manual/dashboard, or an
// undefined deprovision reason) BYPASSES the gate — fail-open by default so a
// new restart reason can never silently get trapped behind the window.
//
// ENG-6568: 'hot-reload-mcp' was REMOVED from this set. It is the class that
// fires when a newly-connected integration changes the .mcp.json server
// membership (and the managed-toolkit / channel-credential-rotation siblings).
// While gated it deferred until the maintenance window AND an idle gap, with no
// cap on the idle re-arm — so a busy agent could sit with the new toolkit
// unloaded indefinitely (valai ~30h; observed across vera/dwight/koda). A
// freshly-added integration silently looking "broken" until a manual bounce is
// a worse failure than interrupting a turn, so this class now BYPASSES the gate
// and respawns on the next poll (same promptness as a manual/dashboard restart).
// TRADEOFF: the window previously also damped the ENG-6123 managed-toolkit
// membership *oscillation* (ENG-6510, still recurring) from restarting customer
// agents repeatedly in business hours. With the window gone for this class, the
// ENG-5441 restart-breaker is the sole flap backstop (it trips + pauses after N
// restarts/window). The correct fix for the oscillation itself is ENG-6510, not
// re-gating integration loads.
//
// ENG-7576: 'integration-change' (the dashboard integration-add stop's breaker
// reason) is DELIBERATELY absent from this set and from the window-exempt set
// below, for exactly the ENG-6568 reason above - it is the same "freshly-added
// integration must load promptly" class as hot-reload-mcp. It looks like a
// gateable-shaped reason; do not "fix" its absence.
const GATEABLE_RESTART_REASONS: ReadonlySet<string> = new Set([
  'model-change',
  'channel-set-change',
  'sender-policy-change',
  'channel-behaviour-change',
  'mcp-presence-reaper',
  // ENG-7949: same gate-but-window-exempt shape as 'mcp-presence-reaper' — see
  // WINDOW_EXEMPT_RESTART_REASONS below for why this one skips the window too.
  'channel-mcp-down',
  // ENG-8343: a value-only credential RE-MINT (canonically the GitHub App
  // installation token, ~1h TTL) rewrites GITHUB_ACCESS_TOKEN/GITHUB_TOKEN, and
  // the ENG-7510 env-only path stops the session to deliver it. Absent from this
  // allowlist it hit the fail-open default above and bypassed BOTH axes, so the
  // stop landed ~3ms after the schedule — unconditionally, mid-turn, roughly once
  // an hour per affected agent (6 agents / ~144 kills a day at time of filing).
  //
  // This absence was an oversight, NOT a decision: ENG-7541 introduced the reason
  // for the breaker and nobody added it here. The call site in manager-worker.ts
  // documented the opposite ("defers until idle (never mid-turn) ... safe on a
  // busy agent"), which is why the gap survived ENG-7541, ENG-7737 and ENG-8264
  // without anyone catching it. Contrast 'integration-change' and 'hot-reload-mcp'
  // above, whose absence IS deliberate and is documented as such.
  //
  // Deliberately NOT given an idle-escalation ceiling like the window axis has
  // (ENG-8224). A repair that can never fire is worse than interrupting a turn
  // when the thing being repaired is a dead channel or a dead toolkit; a rotated
  // credential is not that. The worst case here is an agent that stays busy past
  // the outgoing token's expiry and sees 401s on `gh`/`git` until its next quiet
  // gap, which is strictly cheaper than destroying the turn. Do not "fix" this by
  // forcing the restart through on a deadline.
  'credential-rotation',
]);

/** Is this restart reason subject to the maintenance-window + idle gate? */
export function isGateableRestartReason(reason: string | undefined | null): boolean {
  return reason != null && GATEABLE_RESTART_REASONS.has(reason);
}

// ENG-6491: reasons that are gateable — still defer-until-idle, NEVER interrupt a
// live turn — but exempt from the off-peak maintenance WINDOW. An operator-
// initiated channel add must come online as soon as the agent is quiet, not hours
// later when the window opens (valai/gccm, ENG-6486: a Slack add sat deferred
// until the window opened). Scoped to `channel-set-change`: a channel add stays
// polite (waits for an idle gap) but skips the window.
//
// ENG-6568 note: `hot-reload-mcp` is no longer here OR in GATEABLE_RESTART_REASONS
// — integration loads now bypass the gate entirely (window AND idle), so they are
// not merely window-exempt. This set remains the "gated-but-window-exempt" middle
// tier for channel adds, which still defer until the agent is idle.
// ENG-7771: 'sender-policy-change' joins the same middle tier. It delivers a
// SECURITY control (the slack/teams sender gate reads its env once at boot) -
// an operator who tightens access expects it enforced the same day, not when
// the off-peak window opens hours later, and the whole exposure window of the
// ENG-7771 fail-open was the gap between "console says active" and "running
// process enforces it". Still defer-until-idle: never interrupt a live turn.
// ENG-7949: 'channel-mcp-down' joins the same middle tier. The mcp-presence-
// reaper detected a declared CHANNEL MCP (slack/telegram/msteams/direct-chat/
// ...) with NO live children — the agent is unreachable on that channel right
// now, the same as if it had crashed. Confirmed instance: Koda's slack MCP
// died and sat deferred behind the defer-window for hours until a manual
// force-restart, going completely silent on its primary channel. Still
// defer-until-idle (never interrupt a live turn) — only the off-peak window
// wait is skipped. A routine tool-MCP reaper restart (xero, composio_*, an
// unrecognised key) is NOT in this set and keeps waiting for the window as
// before; only a recognised channel key promotes the reaper's gate reason to
// 'channel-mcp-down' (see manager-worker.ts's reaper `stopSession` callback).
// ENG-8343: 'credential-rotation' joins the same middle tier. The credential it
// delivers expires within the hour, so holding it for the off-peak window would
// strand the agent on a token that dies before the window opens — the window
// wait is longer than the thing it is waiting to deliver. Still defer-until-idle:
// the whole point of gating this class is that it must never interrupt a turn.
const WINDOW_EXEMPT_RESTART_REASONS: ReadonlySet<string> = new Set([
  'channel-set-change',
  'sender-policy-change',
  'channel-mcp-down',
  'credential-rotation',
]);

/**
 * ENG-8224: consecutive `defer-window` decisions after which a gateable restart
 * stops waiting for the maintenance window.
 *
 * The failure this exists to stop: dwight (alyve-host-1, 2026-07-28) lost its
 * `anchor-browser` MCP at 16:00. The reaper decided a session restart was needed
 * and the window gate deferred it — roughly 800 times at ~30s intervals, for 7.7
 * hours, unbroken. The agent stayed `active` with a fresh heartbeat and raised no
 * critical alert while completing zero cards and silently missing three of a named
 * end user's daily scheduled tasks. It was only released when someone removed the
 * integration entirely, which fired a `hot-reload-mcp` respawn — a path ENG-6568
 * had exempted from the gate. Two repair paths, one blocked forever, one not
 * gated at all, and the ungated one won by luck.
 *
 * Why a streak ceiling rather than another name-based exemption: this is the
 * THIRD time we've concluded that a repair which can never fire is worse than
 * interrupting a turn. ENG-6568 carved out `hot-reload-mcp`; ENG-7949 carved out
 * channel MCPs via `channel-mcp-down`. Each carve-out was correct and each left
 * the next class stranded — `anchor-browser` is not a channel key, so it kept the
 * unbounded wait channels no longer have. A ceiling generalises: any gateable
 * class, including ones not yet invented, eventually escalates without anyone
 * having to predict it and add a name to a set.
 *
 * 20 × ~30s ≈ 10 minutes of window-waiting before escalating. Deliberately the
 * same constant as {@link FORCED_UPDATE_DEFER_ESCALATE_AFTER} (ENG-8079), which
 * solved the identical "deferred indefinitely" shape one axis over.
 *
 * Escalation drops ONLY the window axis. The defer-until-idle gate still applies,
 * so a live turn is never interrupted — an escalated restart waits for a quiet
 * gap, it does not barge in. That preserves the ENG-6692 safety property while
 * removing the unbounded wait.
 */
export const RESTART_WINDOW_DEFER_ESCALATE_AFTER = 20;

/**
 * ENG-8224: has a gateable restart been window-deferred often enough that it
 * should stop waiting for the window? Escalation drops the window axis only; the
 * idle gate still governs, so this can never interrupt a live turn.
 */
export function shouldRelaxRestartWindowGate(deferStreak: number): boolean {
  return deferStreak >= RESTART_WINDOW_DEFER_ESCALATE_AFTER;
}

/**
 * ENG-8224: the streak transition, as a pure function so the rule is testable
 * without driving the manager's poll loop.
 *
 * Three distinct cases, and each is load-bearing:
 *
 *  - `proceed`      → **reset**. The restart happened; nothing is owed.
 *  - `defer-window` → **increment**. This is the only decision the ceiling is
 *                     counting, because the window is the only axis it relaxes.
 *  - `defer-idle`   → **carry unchanged**. Neither increment nor reset.
 *
 * The `defer-idle` case is the subtle one and it took a CodeRabbit catch to get
 * right. Incrementing on it is wrong: an agent that stays busy for 20 polls
 * *inside* a maintenance window would bank a full streak without ever having
 * been window-deferred, then bypass the window the moment it went idle outside
 * one — an escalation it never earned. Resetting on it is also wrong: after
 * escalation the decision necessarily becomes `defer-idle` or `proceed`, so a
 * reset would re-arm the window axis on the next poll and the pair would
 * oscillate at the threshold, never escalating for real. Carrying it unchanged
 * is the only option that preserves both properties.
 */
export function nextWindowDeferStreak(
  priorStreak: number,
  decision: 'proceed' | 'defer-window' | 'defer-idle',
): number {
  if (decision === 'proceed') return 0;
  if (decision === 'defer-window') return priorStreak + 1;
  return priorStreak;
}

/**
 * Is this restart reason exempt from the maintenance-WINDOW check while still
 * subject to the defer-until-idle gate? When true, `decideRestartGate` skips the
 * `defer-window` branch but the transcript/pane + inbound idle checks still apply.
 */
export function isWindowExemptRestartReason(reason: string | undefined | null): boolean {
  return reason != null && WINDOW_EXEMPT_RESTART_REASONS.has(reason);
}

/**
 * Gate a non-urgent restart-causing maintenance action.
 *  - 'proceed'      → restart now (in-window or gating disabled, AND idle).
 *  - 'defer-window' → outside the maintenance window; retry next window.
 *  - 'defer-idle'   → in-window but the agent is mid-turn / recently active
 *                     (pane.log younger than the idle threshold); retry soon.
 *
 * The window check fails open when no window is known yet (no heartbeat), but
 * defer-until-idle still applies — never interrupt a live turn.
 *
 * Idle requires BOTH the progress axis AND the inbound channel to be quiet.
 *  - Progress axis (ENG-6264): the session transcript JSONL is the primary
 *    "model is actually producing tokens" signal (transcript younger than
 *    `transcriptStaleSeconds` ⇒ busy), mirroring the wedge detector's
 *    `isSessionProducing` (ENG-6238). pane.log mtime is the FALLBACK, used only
 *    when the transcript age is null (no live session id / unstat-able file) —
 *    pane-primary mis-read a hung turn (static transcript, animated spinner) as
 *    busy, which is exactly what let the reaper never interrupt a wedged agent
 *    yet always interrupt a producing one.
 *  - Inbound axis (ENG-5974): a message arrived <`inboundQuietSeconds` ago but
 *    the agent hasn't produced output yet ⇒ busy.
 * "Busy" on either axis defers. A null age on an axis means "no signal" for it
 * → that axis is not busy, so the other governs. AGT_MAINTENANCE_WINDOW_DISABLE
 * bypasses BOTH gates.
 */
export function decideRestartGate(opts: {
  window: CachedMaintenanceWindow | null;
  paneLogAgeSeconds: number | null;
  inboundAgeSeconds: number | null;
  now: Date;
  env?: NodeJS.ProcessEnv;
  idleThresholdSeconds?: number;
  inboundQuietSeconds?: number;
  /**
   * ENG-6264: age (s) of the running session's transcript JSONL, or null when
   * it can't be resolved (no live session id / unstat-able file). When provided
   * non-null it is the PRIMARY busy signal; null falls back to pane.log age.
   */
  transcriptAgeSeconds?: number | null;
  transcriptStaleSeconds?: number;
  /**
   * ENG-6491: skip the maintenance-WINDOW check for this restart while STILL
   * applying the defer-until-idle gate below. Set for `channel-set-change`
   * (operator channel adds) so a newly-wired channel comes online as soon as the
   * agent is quiet rather than waiting for the off-peak window. Never short-
   * circuits the idle check — a live turn is still never interrupted.
   */
  windowExempt?: boolean;
  /**
   * ENG-8224: how many times IN A ROW this restart has already been deferred by
   * the window. At/above {@link RESTART_WINDOW_DEFER_ESCALATE_AFTER} the window
   * axis is dropped — see {@link shouldRelaxRestartWindowGate}. Omit (or 0) for
   * callers that don't track a streak; they keep the pre-ENG-8224 behaviour.
   */
  windowDeferStreak?: number;
}): 'proceed' | 'defer-window' | 'defer-idle' {
  if (isMaintenanceWindowDisabled(opts.env)) return 'proceed';
  const windowAxisApplies =
    !opts.windowExempt && !shouldRelaxRestartWindowGate(opts.windowDeferStreak ?? 0);
  if (windowAxisApplies && opts.window && !isWithinMaintenanceWindow(opts.window, opts.now)) {
    return 'defer-window';
  }
  const paneThreshold = opts.idleThresholdSeconds ?? RESTART_IDLE_THRESHOLD_SECONDS;
  const inboundThreshold = opts.inboundQuietSeconds ?? RESTART_INBOUND_QUIET_SECONDS;
  const transcriptThreshold = opts.transcriptStaleSeconds ?? RESTART_TRANSCRIPT_STALE_SECONDS;
  // ENG-6264: progress axis — transcript growth is primary, pane.log mtime the
  // fallback when transcript age is null (mirrors ENG-6238 isSessionProducing).
  const transcriptAge = opts.transcriptAgeSeconds ?? null;
  const progressBusy = transcriptAge !== null
    ? transcriptAge < transcriptThreshold
    : opts.paneLogAgeSeconds !== null && opts.paneLogAgeSeconds < paneThreshold;
  const inboundBusy = opts.inboundAgeSeconds !== null && opts.inboundAgeSeconds < inboundThreshold;
  if (progressBusy || inboundBusy) return 'defer-idle';
  return 'proceed';
}
